import type {
  CommonResponseSlicePaperResponse,
  CreateMyDbCustomPaperData,
  CreateMyDbSchoolTestPaperData,
  CustomPaperRequest,
  DeletePaperData,
  MyDbPaperShareScopeUpdateRequest,
  MyDbPaperTitleUpdateRequest,
  Pageable,
  PaperBox as PaperBoxRequest,
  RetrieveByAcademyIdParams,
  SchoolTestPaperRequest,
  UpdateShareScopeData,
  UpdateTitleData,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'
import type { WithDefaultedOptionals, WithNullableOptionals } from '~/http/request'
import type { Slice } from '~/schema/slice'

import { adaptCreatedPaper, adaptPapers } from './adapters'
import type { CreatedPaper, Paper } from './schemas'

// 생성 타입은 Spring Pageable 을 `pageable` 객체로 그리지만 실제 쿼리는 page·size·sort 를 평탄하게 받는다.
// 서버는 제목·학교급 조건을 nullable 로 받는다. 2026-10-13 백엔드 develop(cc5f60e17) 의
// paper/controller/PaperController.kt 로 확인.
export type PaperListParams = WithNullableOptionals<Omit<RetrieveByAcademyIdParams, 'pageable'>> &
  Pageable

// 박스의 nullable(topicId·subTopicId·mappedProblemId·sourceProblemId·sourceWorkbookId·modelVersion)과
// 자료 요청의 시행 학년·학기를 스펙이 optional 로만 그린다. 저장·OCR·보정 방식·묶음·원본 여부는 서버 생성자
// 기본값이 있어 생략할 수 있다. 2026-10-13 백엔드 develop(cc5f60e17) 의 paper/controller/request/SchoolTestPaperRequest.kt·
// CustomPaperRequest.kt, paper/model/dto/PaperBox.kt 로 확인.
export type PaperBoxBody = WithNullableOptionals<PaperBoxRequest>

type _Defaulted =
  | 'saveOriginalProblem'
  | 'ocrMode'
  | 'imageCorrection'
  | 'problemGroups'
  | 'originality'
type _PaperBody<R extends { boxes: PaperBoxRequest[] }> = Omit<
  WithDefaultedOptionals<WithNullableOptionals<R>, Extract<keyof R, _Defaulted>>,
  'boxes'
> & { boxes: PaperBoxBody[] }

export type SchoolTestPaperBody = _PaperBody<SchoolTestPaperRequest>
export type CustomPaperBody = _PaperBody<CustomPaperRequest>
export type PaperOriginality = SchoolTestPaperRequest['originality']

export const getPapers = async (
  params: PaperListParams,
  meta?: RequestMeta,
): Promise<Slice<Paper>> => {
  const body = await httpClient().get<CommonResponseSlicePaperResponse>('/papers', { params, meta })
  return adaptPapers(body.data)
}

// 나의DB 로 만드는 v2 는 CommonResponse 봉투 없이 본문을 바로 낸다.
export const createSchoolTestPaper = async (
  paper: SchoolTestPaperBody,
  meta?: RequestMeta,
): Promise<CreatedPaper> =>
  adaptCreatedPaper(
    await httpClient().post<CreateMyDbSchoolTestPaperData>('/v2/papers/by-school-test', paper, {
      meta,
    }),
  )

export const createCustomPaper = async (
  paper: CustomPaperBody,
  meta?: RequestMeta,
): Promise<CreatedPaper> =>
  adaptCreatedPaper(
    await httpClient().post<CreateMyDbCustomPaperData>('/v2/papers/by-custom', paper, { meta }),
  )

export const deletePaper = async (paperId: Paper['id'], meta?: RequestMeta): Promise<void> => {
  await httpClient().delete<DeletePaperData>(`/papers/${paperId}`, { meta })
}

/***************** 나의DB 자료 관리 *****************/
// 본인이 올린 나의DB 자료만 바꿀 수 있다. 공유 범위는 ACADEMY·TEACHER 만 받고 ACADEMY_GROUP 은 거부한다.
// 두 요청 모두 본문 없이 204 를 낸다. 2026-10-14 백엔드 develop(6a0f8e7bb) 의 paper/controller/MyDbPaperController.kt·
// request/MyDbPaperShareScopeUpdateRequest.kt·MyDbPaperTitleUpdateRequest.kt 로 확인.
export type PaperTitleBody = MyDbPaperTitleUpdateRequest
export type PaperShareScopeBody = Omit<MyDbPaperShareScopeUpdateRequest, 'shareScope'> & {
  shareScope: Exclude<MyDbPaperShareScopeUpdateRequest['shareScope'], 'ACADEMY_GROUP'>
}

export const updatePaperTitle = async (
  paperId: Paper['id'],
  body: PaperTitleBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<UpdateTitleData>(`/my-db-papers/${paperId}/title`, body, { meta })
}

export const updatePapersShareScope = async (
  body: PaperShareScopeBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<UpdateShareScopeData>('/my-db-papers/share-scope', body, { meta })
}
